import { Grid } from "@mui/material";
import { Emptyable } from "@simplyk/common";

import { StrongAuthModalSubmitData } from "../../../../common/contexts/StrongAuthContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export interface PasswordContentProps {
  password: string;
  isSubmitting: boolean;
  error: Emptyable<string>;
  onSubmit: (data: StrongAuthModalSubmitData) => Promise<{ error: { code: string; message?: string } } | null>;
  setPassword: (value: string) => void;
  setIsSubmitting: (value: boolean) => void;
  setError: (value: Emptyable<string>) => void;
}

export const PasswordContent = ({
  password,
  isSubmitting,
  error,
  onSubmit,
  setPassword,
  setIsSubmitting,
  setError,
}: PasswordContentProps) => {
  const { t } = useTranslate();

  const handleSubmit = async () => {
    if (!password) {
      setError(t("common", "password.errorPasswordRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const result = await onSubmit({ password });

      if (result?.error) {
        setError(t("common", "password.errorInvalidPassword"));
        setIsSubmitting(false);
      }
    } catch {
      setError(t("common", "password.errorInvalidPassword"));
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && password && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <>
      <Grid item>
        <DashboardTypography variant="body2">{t("common", "password.description.password")}</DashboardTypography>
      </Grid>

      <Grid item sx={{ width: "100%" }}>
        <DashboardTextField
          data-test="password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t("common", "password.placeholder")}
          autoFocus
        />
      </Grid>

      {error && (
        <Grid item sx={{ width: "100%" }}>
          <DashboardTypography variant="body2" sx={{ color: (theme) => theme.palette.error.main }}>
            {error}
          </DashboardTypography>
        </Grid>
      )}

      <Grid item container direction="column" spacing={1}>
        <Grid item>
          <DashboardButton
            data-test="submit-password-button"
            vibe="brand"
            variant="filled"
            fullWidth
            type="submit"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!password || isSubmitting}
          >
            {t("common", "password.submit")}
          </DashboardButton>
        </Grid>
      </Grid>
    </>
  );
};
